/**
 * The two discs the confirmation draws, and which of them gets letters at all.
 *
 * The sent face is a picture of a connection: the trainer on the left, the person they just invited
 * on the right, and a line drawn between them. Neither end is read off the wire — the client does
 * not exist as a `person` until the roster refetches, and the trainer's own row is not worth a
 * request for two letters. Both come from names already in hand.
 *
 * ── The LETTERS are not this module's rule ───────────────────────────────────────────────────
 * `shared/lib/displayInitials` already owns "how a name becomes two characters", promoted there on
 * 9 September 2026 for exactly the person this face draws on the left: the signed-in trainer, whose
 * identity comes out of the session rather than out of a `PersonRef`. A second rule here would
 * drift the first time one of them met a hyphenated surname, and the first version of this file
 * WAS that second rule.
 *
 * What is left is the decision that module cannot make: **whether there is a name to draw at all.**
 * `displayInitials` answers a blank name with `'?'`, which is right for an avatar that must always
 * show something and wrong here — a trainer whose Supabase account has no profile row yet should
 * get the brand mark, not a question mark inside their own disc. So this returns `null` and the
 * face branches on it.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 */

import { displayInitials } from '~/shared/lib/displayInitials';

/**
 * A person as this feature holds them: two fields, either of which may be empty. The trainer's come
 * from the session, the client's from the form the trainer has just filled in.
 */
export interface Namesake {
  readonly firstName: string;
  readonly lastName: string;
}

export interface InviteMonograms {
  /** `null` when the signed-in trainer has no name to take letters from. The face draws the brand mark. */
  readonly trainer: string | null;
  /** Never `null`: the form refuses an empty name, so the sent face always has both letters. */
  readonly client: string;
}

export function inviteMonograms(trainer: Namesake | null, client: Namesake): InviteMonograms {
  return {
    trainer: trainer === null ? null : monogram(trainer),
    client: monogram(client) ?? '',
  };
}

/** `null` when there is no name at all — see the header for why that is not `'?'`. */
function monogram({ firstName, lastName }: Namesake): string | null {
  const name = `${firstName} ${lastName}`.trim();
  return name === '' ? null : displayInitials(name);
}
